import DOMPurify from 'dompurify'

/**
 * Composable for cleaning/sanitizing email message bodies and preparing inline image handling.
 */
export function useEmailBodyProcessor() {
    function buildCidPlaceholder(attachment, message) {
        const token = `CID_PLACEHOLDER_${attachment.content_id}_${attachment.id}_${message.id}`
        const dataUrl = `data:text/plain;base64,${btoa(token)}`
        return { token, dataUrl }
    }

    function replaceCidSrcs(html, cidAttachments, message) {
        const cidPlaceholders = []

        if (cidAttachments.length === 0) {
            return { html, cidPlaceholders }
        }

        let output = html

        cidAttachments.forEach((att) => {
            const { token, dataUrl } = buildCidPlaceholder(att, message)
            const cidPattern = new RegExp(`cid:${escapeRegExp(att.content_id)}`, 'gi')

            if (cidPattern.test(output)) {
                output = output.replace(cidPattern, dataUrl)
                cidPlaceholders.push({ attachment: att, token, dataUrl })
            }
        })

        return { html: output, cidPlaceholders }
    }

    function injectInlineImagePlaceholders(html, simpleImageAttachments, message) {
        const inlinePlaceholders = []

        if (simpleImageAttachments.length === 0) {
            return { html, inlinePlaceholders }
        }

        const blocks = simpleImageAttachments.map((att, idx) => {
            const id = `inline-image-${message.id}-${idx}`
            inlinePlaceholders.push({ id, attachment: att })
            return `<div id="${id}" style="margin:10px 0;text-align:center;color:#666;font-size:14px;">Loading image: ${att.filename || 'Image'}...</div>`
        })

        const content = blocks.join('')
        const output = html && html.trim() ? `${html}${content}` : content

        return { html: output, inlinePlaceholders }
    }

    function gentlyCleanRepliesAndSignatures(html) {
        let body = html.replace(/\n--\s*\n[\s\S]*$/g, '').replace(/\r\n--\s*\r\n[\s\S]*$/g, '')

        const indicators = [
            /gmail_quote/i.test(body),
            /Original Message/i.test(body),
            /On .{0,200}wrote:/i.test(body),
            /From:/i.test(body) && /Sent:/i.test(body),
        ].filter(Boolean).length

        if (indicators >= 2) {
            body = body
                .replace(/<blockquote[^>]*class="[^"]*gmail_quote[^"]*"[\s\S]*?<\/blockquote>/gi, '')
                .replace(/[\-=_]{10,}[\s\S]*?Original[\s\S]*?Message[\s\S]*$/gi, '')
                .replace(/[\-=_]{10,}[\s\S]*?Forwarded[\s\S]*?Message[\s\S]*$/gi, '')
        }

        return body
            .replace(/(<br\s*\/?>){3,}/gi, '<br><br>')
            .replace(/\n{3,}/g, '\n\n')
            .replace(/(<br\s*\/?>|\s)*$/gi, '')
            .replace(/^\s*(<br\s*\/?>|\s)*/gi, '')
            .replace(/<p>\s*<\/p>/gi, '')
            .replace(/<div>\s*<\/div>/gi, '')
    }

    function injectSafetyStyles(html) {
        const style = `
      <style>
        img { max-width: 100% !important; height: auto !important; }
        table { max-width: 100% !important; table-layout: fixed !important; word-wrap: break-word !important; }
        td, div, p { word-wrap: break-word !important; overflow-wrap: break-word !important; }
        * { box-sizing: border-box !important; }
      </style>
    `

        return style + html
    }

    function sanitize(html) {
        return DOMPurify.sanitize(html, {
            ALLOWED_TAGS: [
                'p',
                'br',
                'div',
                'span',
                'strong',
                'em',
                'u',
                'i',
                'b',
                'a',
                'img',
                'ul',
                'ol',
                'li',
                'blockquote',
                'h1',
                'h2',
                'h3',
                'h4',
                'h5',
                'h6',
                'table',
                'tr',
                'td',
                'th',
                'thead',
                'tbody',
                'tfoot',
                'center',
                'font',
                'small',
                'big',
                'sub',
                'sup',
                'hr',
                'pre',
                'code',
                'style',
            ],
            ALLOWED_ATTR: [
                'href',
                'src',
                'alt',
                'title',
                'target',
                'style',
                'class',
                'id',
                'width',
                'height',
                'border',
                'cellpadding',
                'cellspacing',
                'align',
                'color',
                'bgcolor',
                'size',
                'face',
                'valign',
            ],
            ALLOW_DATA_ATTR: false,
            FORBID_TAGS: ['script', 'object', 'embed', 'form', 'input', 'button', 'iframe'],
            FORBID_ATTR: ['onclick', 'onload', 'onerror', 'onmouseover', 'onfocus', 'onblur', 'onsubmit'],
        })
    }

    function textToHtml(text) {
        if (!text) {
            return ''
        }

        const escaped = text.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')

        return escaped.replace(/\n/g, '<br>')
    }

    function cleanBody(message) {
        const attachments = Array.isArray(message?.attachments) ? message.attachments : []
        const bodyRaw = message?.body_html || textToHtml(message?.body_text || '')

        const imageCid = attachments.filter((a) => isImage(a) && !!a.content_id)
        const imageSimple = attachments.filter((a) => isImage(a) && !a.content_id)

        const cidStep = replaceCidSrcs(bodyRaw, imageCid, message)
        const inlineStep = injectInlineImagePlaceholders(cidStep.html, imageSimple, message)
        const cleaned = gentlyCleanRepliesAndSignatures(inlineStep.html)
        const withStyles = injectSafetyStyles(cleaned)
        const html = sanitize(withStyles)

        return {
            html,
            cidPlaceholders: cidStep.cidPlaceholders,
            inlinePlaceholders: inlineStep.inlinePlaceholders,
        }
    }

    function isImage(att) {
        if (!att?.content_type) {
            return false
        }

        return att.content_type.toLowerCase().startsWith('image/')
    }

    function getImageAttachments(attachments = []) {
        return attachments.filter((att) => {
            return isImage(att)
        })
    }

    function getNonImageAttachments(attachments = []) {
        return attachments.filter((att) => {
            return !isImage(att)
        })
    }

    function escapeRegExp(str = '') {
        return str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
    }

    return {
        cleanBody,
        getImageAttachments,
        getNonImageAttachments,
    }
}
